import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";

import moment from 'moment';
import  { v4 as uuidv4 } from 'uuid';

import { productStockUpdate, productsUpdate, getProductsById } from '../services/productService';
import { getCharacteristicAll, getCharacteristicsSearch } from '../services/characteristics';

import { Box, Typography, TextField, IconButton, Select, MenuItem, InputAdornment, Button, Grid } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import TitleText from '../components/TitleText';
import AutocompleteSearch from '../components/AutocompleteSearch';
import ClearIcon from '../components/icons/ClearIcon';
import { buildTree, normalizeText, findElementByCharacteristics } from '../utils/utils';
import DeleteModal from '../components/DeleteModal';
import ProductVariantResume from '../components/ProductVariantResume';
import ButtonStyled from '../components/ButtonStyled';
import CheckIcon from '@mui/icons-material/Check';

import { useSnackbar } from 'notistack';
import NumberInput from '../components/inputs/NumberInput';
import TextInput from '../components/inputs/TextInput';
import ShowLoading from '../components/ShowLoading';


const styles = {
  textList: {
    color: '#252323',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 'normal',
    pb: '1.5rem'
  },
  titleInput:{
    color: '#A1A1A1',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    pb:'0.625rem'
  },
  card:{
    borderRadius: '0.25rem',
    background: '#FFF',
    display: 'flex',
    padding: '1.5rem',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  input: {
    "& .MuiInputBase-root input": { padding:'0.625rem 1rem !important' },
    "& .MuiInputBase-input:disabled": {
      color: 'black !important',
      WebkitTextFillColor: '#303030'
    }
  },
  characteristicTableContainer: {
    display:'flex',
    flexDirection:'column',
    gap:'1.5rem',
    width:'100%',
    p: '1.5rem'
  },
  characteristicTableRow: {
    display:'flex',
    flexDirection:{ xs:'column', lg:'row' },
    justifyContent:'space-between',
    width:'100%',
    height: '100%',
    alignItems: 'flex-end',
    border: { lg:'none', xs:'1px solid #E0E0E0' },
    borderRadius: { lg:'0', xs:'0.5rem' },
    p: { lg:'0', xs:'1.5rem' },
    gap: '1.5rem',
  },
  characteristicTableCell: {
    width: { lg:'25%', xs:'100%' },
  },
  characteristicGrid: {
    display:'flex',
    gap: '1.5rem',
    width:'100%'
  },
  characteristicGridItem: {
    width:'20%'
  },
  iconButton: {
    backgroundColor: '#FC581F',
    borderRadius: '12px',
    color: 'white',
    alignSelf: 'flex-end',
    "&:hover": {
      backgroundColor: '#c04c25ff',
    }
  }
}

const ProductStockForm = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [filteredOptions, setFilteredOptions] = useState([]);

    const product = state && state.product ? state.product : '';

    const [ assignedCharacteristics, setAssignedCharacteristics ] = useState([]);
    const [ primaryCharacteristic, setPrimaryCharacteristic ] = useState(null);

    const [characteristics, setCharacteristics] = useState([]);
    const [characteristicForm, setCharacteristicForm] = useState([]);
    const [stockForm, setStockForm] = useState('');
    const [extraDeliveryTimeForm, setExtraDeliveryTimeForm] = useState('');
    const [characteristicFormErrors, setCharacteristicFormErrors] = useState([]);
    const [stockFormError, setStockFormError] = useState(false);
    const [extraDeliveryTimeFormError, setExtraDeliveryTimeFormError] = useState(false);

    const [ flatValueCodeSet, setFlatValueCodeSet ] = useState(new Set());
    const [ isLoading, setIsLoading ] = useState(false);

    const [openModal, setOpenModal] = useState(false);
    const [stepper, setStepper] = useState(0);
    const { enqueueSnackbar } = useSnackbar();
    
    
    useEffect(() => {
        loadData();
    
    }, [])

    const loadData = async () => {
        if(!product) return;

        setIsLoading(true);

        // console.log("BASE PRODUCT", product)

        const productStockResult = await getProductsById(product._id);

        if (!productStockResult.Product) {
            setIsLoading(false);
            return;
        }

        const productStock = productStockResult.Product;
        // console.log("FETCHED PRODUCT", productStock)
        // console.log("LOADED PRODUCT", product)

        if(productStock && !!productStock.characteristics){
            const primaryCharacteristic = productStock.characteristics.find(characteristic => characteristic.primary) || {};
            setPrimaryCharacteristic({
                ...primaryCharacteristic,
                values: primaryCharacteristic?.values?.map(value => ({
                    ...value,
                    stock: value.stock || 0,
                    extraDeliveryTime: value.extraDeliveryTime || 0
                })) || []
            });

            const assignedCharacteristics = productStock.characteristics.filter(characteristic => !characteristic.primary);
            // console.log("assignedCharacteristics", assignedCharacteristics)

            // Get flat set of value codes to determine which values to show
            const valueCodesFlatSet = new Set(assignedCharacteristics.flatMap(characteristic => characteristic?.values?.map(value => value.code)));
            
            // console.log("valueCodesFlatSet", valueCodesFlatSet)
            setFlatValueCodeSet(valueCodesFlatSet)

            // Get all characteristics and filter the ones that are not primary
            const allCharacteristics = await getCharacteristicAll();
            // console.log("allCharacteristics", allCharacteristics)

            // Match the assigned characteristics with the all characteristics
            const matchedCharacteristics = assignedCharacteristics.map(characteristic => {
                const matchedCharacteristic = allCharacteristics?.items?.find(c => c._id === characteristic.characteristicId);
                return {
                    ...matchedCharacteristic,
                    values: matchedCharacteristic?.values?.map(value => ({
                        ...value,
                        stock: value.stock || 0,
                        extraDeliveryTime: value.extraDeliveryTime || 0
                    }))
                };

            });

            // console.log("matchedCharacteristics", matchedCharacteristics)

            // Set matched assigned characteristics
            setAssignedCharacteristics(matchedCharacteristics.map(characteristic => ({
                ...characteristic,
                values: characteristic?.values?.map(value => ({
                    ...value,
                    stock: value.stock || 0,
                    extraDeliveryTime: value.extraDeliveryTime || 0
                }))
            })));
        }
        setIsLoading(false);
    };

    const handleValueChange = (e, index, field) => {
        let newValue = parseInt(e.target.value);
        if(isNaN(newValue) || newValue < 0){
            newValue = 0;
        }

        const newCharacteristic = [...primaryCharacteristic?.values];
        newCharacteristic[index][field] = newValue;
        setPrimaryCharacteristic({...primaryCharacteristic, values: newCharacteristic});
    };

    const handleGlobalValueChange = (e, charIndex, valIndex, field) => {
        let newValue = parseInt(e.target.value);
        if(isNaN(newValue) || newValue < 0){
            newValue = 0;
        }

        const updatedCharacteristics = [...assignedCharacteristics];
        updatedCharacteristics[charIndex].values[valIndex][field] = newValue;
        setAssignedCharacteristics(updatedCharacteristics);
    }

    const onSubmit = async () => {
        setIsLoading(true);

        // Filter only values in the flatValueCodeSet
        const filteredAssignedCharacteristics = assignedCharacteristics.map(characteristic => ({
            characteristicId: characteristic._id,
            characteristicName: characteristic.characteristicName,
            characteristicCategory: characteristic.characteristicCategory,
            code: characteristic.code,
            primary: false,
            values: characteristic?.values
                .filter(value => flatValueCodeSet.has(value.code))
                .map(v => ({
                    valueName: v.valueName,
                    code: v.code
                }))
        }));

        // console.log("filteredAssignedCharacteristics", filteredAssignedCharacteristics)

        // Update product with updated primary stock and only include value code and valueName
        const productToUpdate = {
            ...product,
            characteristics: [
                primaryCharacteristic,
                ...filteredAssignedCharacteristics
            ]
        }

        // console.log("productToUpdate", productToUpdate, assignedCharacteristics)

        const response = await productStockUpdate({
            product: productToUpdate, 
            characteristics: assignedCharacteristics
        })

        setIsLoading(false);
        if(response.result){
            enqueueSnackbar("Stock actualizado exitosamente.", {
                variant: 'success',
                style: { 
                    backgroundColor: '#FC581F',
                    Width: '100%',
                    justifyContent: 'center'
                }
            });
            setOpenModal(false);
            navigate('/admin/products');

        }else{
            enqueueSnackbar("Error al actualizar el stock.", {
                variant: 'error'
            });
        }
    }

    const onCloseModal = () => {
        setOpenModal(false);
    }

    return (
        <Box sx={{px:{xs:'1.5rem', md:'3rem'}, width:'100%' }} >
        <TitleText title={"INVENTARIO DE PRODUCTO"}/>
            {isLoading && <ShowLoading text="Cargando inventario..." />}
            {!isLoading && (
                <>
                    <Box sx={styles.card}>
                        <Typography sx={styles.titleInput}>PRODUCTO BASE</Typography>
                        <TextField disabled value={product.productName} fullWidth size='small' variant="outlined" sx={styles.input}/>
                    </Box>
                    <TitleText title={'STOCK'}/>
                    <Typography sx={styles.textList}>Stock actual de valores de las características asignadas al producto.</Typography>
                    <Box sx={styles.card}>
                        <Typography sx={styles.titleInput}>Stock del producto por {primaryCharacteristic?.characteristicName}</Typography>
                        <Box sx={styles.characteristicTableContainer}>
                            {primaryCharacteristic?.values.map((value, index) => (
                                <Box key={index} sx={styles.characteristicTableRow}>
                                    <Box sx={styles.characteristicTableCell}>
                                        <TextInput
                                            label={"Característica Principal"}
                                            value={primaryCharacteristic?.characteristicName}
                                            disabled={true}
                                        />
                                    </Box>
                                    <Box sx={styles.characteristicTableCell}>
                                        <TextInput
                                            label={"Valor"}
                                            value={value.valueName}
                                            disabled={true}
                                        />
                                    </Box>
                                    <Box sx={styles.characteristicTableCell}>
                                        <TextInput
                                            label={"Código de Valor"}
                                            value={value.code}
                                            disabled={true}
                                        />
                                    </Box>
                                    <Box sx={{...styles.characteristicTableCell, width: { lg:'15%', xs:'100%' }}}>
                                        <NumberInput
                                            label={"Stock"}
                                            value={value.stock}
                                            onCommit={(e) => handleValueChange(e, index, 'stock')}
                                            isError={!!value.stockError}
                                            unit='pzas'
                                        />
                                    </Box>
                                    <Box sx={{...styles.characteristicTableCell, width: { lg:'15%', xs:'100%' }}}>
                                        <NumberInput
                                            label={"Tiempo Estimado de Entrega"}
                                            value={value.extraDeliveryTime ?? 0}
                                            onCommit={(e) => handleValueChange(e, index, 'extraDeliveryTime')}
                                            isError={!!value.stockError}
                                            unit='semanas'
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box sx={styles.card}>
                        <Typography sx={styles.titleInput}>Stock global de los accesorios</Typography>
                        <Box sx={styles.characteristicTableContainer}>
                            {assignedCharacteristics && assignedCharacteristics?.map((characteristic, index) => (
                                characteristic?.values?.map((value, valIndex) => (
                                    flatValueCodeSet.has(value.code) && (
                                        <Box key={valIndex} sx={{...styles.characteristicTableRow}}>
                                            <Box sx={styles.characteristicTableCell}>
                                                <TextInput
                                                    label={"Característica"}
                                                    value={characteristic?.characteristicName}
                                                    disabled={true}
                                                    inputProps={{ sx: { backgroundColor: '#F5F5F5', width: '100%', height: '100%' } }}
                                                />
                                            </Box>
                                            <Box sx={styles.characteristicTableCell}>
                                                <TextInput
                                                    label={"Valor"}
                                                    value={value.valueName}
                                                    disabled={true}
                                                />
                                            </Box>
                                            <Box sx={styles.characteristicTableCell}>
                                                <TextInput
                                                    label={"Código de Valor"}
                                                    value={value.code}
                                                    disabled={true}
                                                />
                                            </Box>
                                            <Box sx={{...styles.characteristicTableCell, width: { lg:'15%', xs:'100%' }}}>
                                                <NumberInput
                                                    label={"Stock"}
                                                    value={value.stock}
                                                    onCommit={(e) => handleGlobalValueChange(e, index, valIndex, 'stock')}
                                                    isError={!!value.stockError}
                                                    unit='pzas'
                                                />
                                            </Box>
                                            <Box sx={{...styles.characteristicTableCell, width: { lg:'15%', xs:'100%' }}}>
                                                <NumberInput
                                                    label={"Tiempo Estimado de Entrega"}
                                                    value={value.extraDeliveryTime ?? 0}
                                                    onCommit={(e) => handleGlobalValueChange(e, index, valIndex, 'extraDeliveryTime')}
                                                    isError={!!value.stockError}
                                                    unit='semanas'
                                                />
                                            </Box>
                                        </Box>
                                    )
                                ))
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'flex-end', mt: '1.5rem' }}>
                            <ButtonStyled handleClick={() => setOpenModal(true)} disabled={isLoading} text='Guardar Cambios' color='primary' />
                        </Box>
                    </Box>
                </>
            )}
            <DeleteModal
                openModal={openModal}
                onCloseModal={onCloseModal}
                onConfirmed={onSubmit}
                isLoading={isLoading}
                title='¿Estás seguro de querer actualizar el stock?'
                confirmButtonText='Confirmar'
                isDisabled={isLoading}
            />
        </Box>
    )
}

export default ProductStockForm